import classNames from 'classnames';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Text } from 'components-antd';
import { getDynamicPostFormSelector } from 'store/selectors/requestFormProcess';

import styles from './styles.module.scss';

import { useFetchFormMetaData } from 'pages/FormProcess/hooks/useFetchFormMetaData';
import { dynamicManager } from 'pages/FormProcess/DynamicForm/DynamicManager';

import { FormSignatories } from './components/FormSignatories';
import {
  updatePostFormEffect,
  updateSignatoriesEffect,
  updateSignatureSequenceEffect,
} from 'store/effects/formProcess';
import { createReplacePayload } from './helper';

export interface DynamicPostFormPropsType {
  isBundle?: boolean;
  handleRemoveEditor: (editorId) => void;
  setReplaceEntityModal: (...data) => void;
  updateSignatureSequence: (isSequence, updatedSignatories, cb) => void;
  isReady?: boolean;
  formProcessPublicId?: string;
  formDocumentPublicId?: string;
}

export const DynamicPostForm = ({
  isBundle,
  formProcessPublicId,
  formDocumentPublicId,
  handleRemoveEditor,
}: DynamicPostFormPropsType) => {
  const dynamicPostForm = useSelector(getDynamicPostFormSelector) || {};

  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  useFetchFormMetaData();

  const {
    editorParticipants = [],
    editorsPresent = false,
    isSequentialSigning = false,
    signatoriesPresent = false,
    signatoryParticipants = [],
  } = dynamicPostForm;

  const editors = editorParticipants.filter((e) => !e.done);
  const signers = signatoryParticipants.filter((s) => !s.done);

  const memoizedSigners = useMemo(
    () =>
      signers.map(({ email, userId, ...restItem }) => ({
        ...restItem,
        Email: email,
        UserId: userId,
        ...(!userId && { customName: true }),
      })),
    [signers],
  );

  const [signatories, setSignatories] = useState<any[]>(memoizedSigners);
  const [noteMessage, setNoteMessage] = useState<string>('');
  const [isSequential, setIsSequential] = useState(isSequentialSigning);
  const [hasModifiedInfo, setHasModifiedInfo] = useState(false);

  const [sequenceModified, setSequenceModified] = useState(false);

  let editorSequential = false;
  if (editors.length > 1) {
    const firstSequence = editors?.[0]?.sequence;
    if (firstSequence) {
      editorSequential = !editors.every((e) => e.sequence === firstSequence);
    }
  }

  const updatePostFormDetails = (details) => {
    dispatch(updatePostFormEffect(details));
  };

  const modifySignatories = (signatories) => {
    setHasModifiedInfo(true);
    setSignatories(signatories);

    updatePostFormDetails({ hasModifiedInfo: true, signatories });
  };

  const onUpdateSequence = (updatedSignatories, sequential) => {
    setSignatories(updatedSignatories);
    setIsSequential(sequential);

    updatePostFormDetails({
      sequenceModified: true,
      signatories: updatedSignatories,
      isSequential: sequential,
    });
  };

  const manageSetSequence = () => {
    setSequenceModified(true);
  };

  const isDisabled = () => {
    return signatories.find(({ Name, Email }) => !Name || !Email);
  };

  const manageSignatureSequenceUpdate = (modifiedSignatories) => {
    if (sequenceModified) {
      dynamicManager.updatePostFormSignatureSequence(
        {
          isSequential,
          signatories: modifiedSignatories,
          formProcessPublicId,
          formDocumentPublicId,
        },

        () => {
          dynamicManager.handleSendDocument(null, noteMessage);
        },
      );
    } else {
      dynamicManager.handleSendDocument(null, noteMessage);
    }
  };

  const handleSendDocumentSubmit = () => {
    let formProcessId: string;

    const updatedSignatories = [...signatories];

    try {
      formProcessId = dynamicManager?.getBundleData()?.formProcessPublicId;
    } catch (err) {
      formProcessId = dynamicManager?.getAuthData()?.formProcessPublicId;
    }

    let singatoriesWithNote = updatedSignatories.filter((item) => !!item.note);

    const replacedSignatories = updatedSignatories.filter((item) => item.isReplaced);

    setLoading(true);

    if (!hasModifiedInfo && !sequenceModified) {
      dynamicManager.handleSendDocument(null, noteMessage);
    } else if (!replacedSignatories.length) {
      singatoriesWithNote.length > 0 &&
        dynamicManager.saveNoteDetailsForSignatory(singatoriesWithNote);

      manageSignatureSequenceUpdate(updatedSignatories);
    } else {
      dispatch(
        updateSignatoriesEffect(
          {
            formProcessPublicId: formProcessId,
            Signatories: createReplacePayload(replacedSignatories, isSequential),
          },
          (err, response) => {
            if (!err) {
              const { signatoriesAdded } = response.data.value;

              const emailToSignerMap = signatoriesAdded.reduce((acc, signer) => {
                acc[signer.UserEmail] = signer;
                return acc;
              }, {} as Record<string, typeof signatoriesAdded[0]>);

              const modifiedSignatories = [...updatedSignatories];

              for (const signer of singatoriesWithNote) {
                const matched = emailToSignerMap[signer.Email];
                if (matched) {
                  signer.signatoryId = matched.SignatureRequestId;
                }
              }

              // Update modified Signatories
              for (const signer of modifiedSignatories) {
                const matched = emailToSignerMap[signer.Email];
                if (matched) {
                  signer.signatoryId = matched.SignatureRequestId;
                }
              }

              setTimeout(() => {
                singatoriesWithNote.length > 0 &&
                  dynamicManager.saveNoteDetailsForSignatory(singatoriesWithNote);

                manageSignatureSequenceUpdate(modifiedSignatories);
              }, 500);
            } else {
              setLoading(false);
            }
          },
        ),
      );
    }
  };

  return (
    <Fragment>
      <div className={styles.pageContent}>
        {(editorsPresent || signatoriesPresent) && (
          <Fragment>
            <Text className={styles.questionHeader}>
              This {isBundle ? 'bundle' : 'document'} is going to:
            </Text>

            <FormSignatories
              loading={loading}
              setLoading={setLoading}
              noteMessage={noteMessage}
              signatories={signatories}
              setNoteMessage={setNoteMessage}
              setSignatories={modifySignatories}
              isSequentialSigning={isSequential}
              setSequenceModified={manageSetSequence}
              updateSignatureSequence={onUpdateSequence}
              updatePostFormDetails={updatePostFormDetails}
            />
          </Fragment>
        )}

        {!loading && (
          <Button
            variant="secondary"
            className={classNames(styles.navigationButton, {
              [styles.disabledButton]: isDisabled(),
            })}
            disabled={isDisabled()}
            onClick={handleSendDocumentSubmit}
          >
            Continue
          </Button>
        )}
      </div>
    </Fragment>
  );
};
